import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getInitials, getRandomColor, formatPhoneNumber, shouldShowApiToast } from '../../utils/helpers';
import { useModal } from '../../context/ModalContext';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  getMembers,
  getMember,
  createMember,
  updateMember,
  deleteMember,
  bulkDeleteMembers,
  bulkActionMembers,
  sendSmsToMember,
  clearApiMessage,
  clearError,
  clearMemberState,
  selectMembers,
  selectMemberPagination,
  selectCurrentMember,
  selectMembersLoading,
  selectMembersApiMessage,
  selectMembersError,
} from '../../store/slices/main/memberSlice';
import { getBranches, selectBranches } from '../../store/slices/system/branchSlice';
// IMPORT THE GROUP SLICE
import { getGroups, selectGroups } from '../../store/slices/reports/groupSlice';
import { Download, BarChart3, ClipboardList, FileSpreadsheet, File, AlertTriangle, Pencil, Trash2, Save, User, RefreshCw, Ban, CheckCircle2, Loader2, Search, Mail, Eye } from 'lucide-react';

const Members = ({ showToast }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [branchesLoaded, setBranchesLoaded] = useState(false);

  const editFirstNameRef = useRef(null);
  const editLastNameRef = useRef(null);
  const editPhoneRef = useRef(null);
  const editEmailRef = useRef(null);
  const editDobRef = useRef(null);
  const editGenderRef = useRef(null);
  const editGroupRef = useRef(null);
  const editBranchRef = useRef(null);
  const editStatusRef = useRef(null);
  const [editingMemberId, setEditingMemberId] = useState(null);

  const createFirstNameRef = useRef(null);
  const createLastNameRef = useRef(null);
  const createPhoneRef = useRef(null);
  const createEmailRef = useRef(null);
  const createDobRef = useRef(null);
  const createGenderRef = useRef(null);
  const createGroupRef = useRef(null);
  const createBranchRef = useRef(null);
  const createStatusRef = useRef(null);

  const lastToastRef = useRef({ message: null, timestamp: 0 });

  const members = useSelector(selectMembers) || [];
  const pagination = useSelector(selectMemberPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentMember = useSelector(selectCurrentMember);
  const loading = useSelector(selectMembersLoading);
  const apiMessage = useSelector(selectMembersApiMessage);
  const error = useSelector(selectMembersError);
  const branchList = useSelector(selectBranches) || [];
  // ADD GROUP SELECTOR
  const groupList = useSelector(selectGroups) || [];

  // ==================== HELPER: FORMAT DATE ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Load data on mount
  useEffect(() => {
    dispatch(getMembers({ 
      per_page: itemsPerPage, 
      page: currentPage,
      search: searchTerm,
      group: groupFilter,
      branch: branchFilter,
      status: statusFilter
    }));
    
    // Load groups and branches on mount
    dispatch(getGroups({ per_page: 100 })); // Get all groups
    dispatch(getBranches({ per_page: 100 })); // Get all branches
    
    return () => {
      dispatch(clearMemberState());
    };
  }, [dispatch]);

  // Handle search and filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage
      };
      if (groupFilter) params.group = groupFilter;
      if (branchFilter) params.branch = branchFilter;
      if (statusFilter) params.status = statusFilter;
      
      dispatch(getMembers(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, groupFilter, statusFilter, currentPage, itemsPerPage, dispatch]);

  // UPDATE available groups from group slice
  useEffect(() => {
    if (groupList && groupList.length > 0) {
      // Extract group names from the group objects
      const groupNames = groupList.map(g => g.name || g.title || g.group_name || g.label).filter(Boolean);
      if (groupNames.length > 0) {
        setAvailableGroups(groupNames);
        setGroupsLoaded(true);
      }
    }

    // Also check if pagination has groups (as a fallback)
    if (pagination?.groups && pagination.groups.length > 0) {
      setAvailableGroups(pagination.groups);
      setGroupsLoaded(true);
    }
  }, [groupList, pagination]);

  // UPDATE available branches from branch slice
  useEffect(() => {
    if (branchList && branchList.length > 0) {
      const branchNames = branchList.map(b => b.name || b.title || b.branch_name || b.label).filter(Boolean);
      if (branchNames.length > 0) {
        setAvailableBranches(branchNames);
        setBranchesLoaded(true);
      }
    }

    // Also check if pagination has branches (as a fallback)
    if (pagination?.branches && pagination.branches.length > 0) {
      setAvailableBranches(pagination.branches);
      setBranchesLoaded(true);
    }
  }, [branchList, pagination]);

  // Display API messages - with deduplication
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== PAGINATION ====================

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const getCreateFormValues = () => {
    return {
      first_name: createFirstNameRef.current?.value?.trim() || '',
      last_name: createLastNameRef.current?.value?.trim() || '',
      phone: createPhoneRef.current?.value?.trim() || '',
      email: createEmailRef.current?.value?.trim() || '',
      dob: createDobRef.current?.value || '',
      gender: createGenderRef.current?.value || '',
      group: createGroupRef.current?.value || '',
      branch: createBranchRef.current?.value || '',
      status: createStatusRef.current?.value || 'Active'
    };
  };

  const getEditFormValues = () => {
    return {
      first_name: editFirstNameRef.current?.value?.trim() || '',
      last_name: editLastNameRef.current?.value?.trim() || '',
      phone: editPhoneRef.current?.value?.trim() || '',
      email: editEmailRef.current?.value?.trim() || '',
      dob: editDobRef.current?.value || '',
      gender: editGenderRef.current?.value || '',
      group: editGroupRef.current?.value || '',
      branch: editBranchRef.current?.value || '',
      status: editStatusRef.current?.value || 'Active'
    };
  };

  // Phone number input handler - only accepts numbers and + sign
  const handlePhoneInput = (e) => {
    const value = e.target.value;
    // Allow only numbers and + sign
    const filtered = value.replace(/[^0-9+]/g, '');
    e.target.value = filtered;
    return filtered;
  };

  const validateForm = (values) => {
    const errors = {};
    
    if (!values.first_name) {
      errors.first_name = 'First name is required';
    }
    if (!values.last_name) {
      errors.last_name = 'Last name is required';
    }
    if (!values.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9]{8,15}$/.test(values.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!values.group) {
      errors.group = 'Group is required';
    }
    if (!values.branch) {
      errors.branch = 'Branch is required';
    }

    return errors;
  };

  const openExportModal = () => {
    const data = members;
    if (data.length === 0) {
      showToastMessage('No members to export', 'error');
      return;
    }

    const fields = [
      { key: 'id', label: 'ID' },
      { key: 'member_code', label: 'Member Code' },
      { key: 'full_name', label: 'Full Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'group', label: 'Group' },
      { key: 'branch', label: 'Branch' },
      { key: 'status', label: 'Status' },
      { key: 'gender', label: 'Gender' },
      { key: 'dob', label: 'Date of Birth' },
      { key: 'created_at', label: 'Joined Date' },
      { key: 'engagement_score', label: 'Engagement Score' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export Members</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} member{data.length !== 1 ? 's' : ''} in your preferred format.
            {selectedMembers.length > 0 && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {selectedMembers.length} selected
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const exportData = selectedMembers.length > 0 
                      ? data.filter(m => selectedMembers.includes(m.id))
                      : data;
                    const formattedData = formatForExport(exportData, fields);
                    const filename = getExportFilename('members');
                    
                    switch (format) {
                      case 'CSV':
                        exportToCSV(formattedData, filename);
                        break;
                      case 'JSON':
                        exportToJSON(formattedData, filename);
                        break;
                      case 'Excel':
                        exportToExcel(formattedData, filename);
                        break;
                      case 'PDF':
                        exportToPDF(formattedData, 'Members Report', filename);
                        break;
                      default:
                        showToastMessage('Export format not supported', 'error');
                        return;
                    }
                    showToastMessage(`📥 ${exportData.length} members exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px', display: 'flex' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <FileSpreadsheet size={20} />}
                    {format === 'PDF' && <File size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {selectedMembers.length > 0 ? selectedMembers.length : members.length} records will be exported</span>
            {selectedMembers.length > 0 && (
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => setSelectedMembers([])}
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== HANDLE SELECTION ====================

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(members.map(m => m.id));
      setSelectAll(true);
    } else {
      setSelectedMembers([]);
      setSelectAll(false);
    }
  };

  const handleSelectMember = (id) => {
    if (selectedMembers.includes(id)) {
      setSelectedMembers(selectedMembers.filter(m => m !== id));
    } else {
      setSelectedMembers([...selectedMembers, id]);
    }
  };

  // Handle Add Member
  const handleAddMember = () => {
    // Reset form fields
    setTimeout(() => {
      if (createFirstNameRef.current) createFirstNameRef.current.value = '';
      if (createLastNameRef.current) createLastNameRef.current.value = '';
      if (createPhoneRef.current) createPhoneRef.current.value = '';
      if (createEmailRef.current) createEmailRef.current.value = '';
      if (createDobRef.current) createDobRef.current.value = '';
      if (createGenderRef.current) createGenderRef.current.value = '';
      if (createGroupRef.current) createGroupRef.current.value = '';
      if (createBranchRef.current) createBranchRef.current.value = '';
      if (createStatusRef.current) createStatusRef.current.value = 'Active';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><User size={16} style={{ verticalAlign: '-3px' }} /> Add New Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="Emmanuel" 
                ref={createFirstNameRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="Asante" 
                ref={createLastNameRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="+233244123456" 
                ref={createPhoneRef}
                onChange={(e) => handlePhoneInput(e)}
                onKeyPress={(e) => {
                  // Only allow numbers and + sign
                  const charCode = e.which ? e.which : e.keyCode;
                  if (charCode !== 43 && (charCode < 48 || charCode > 57)) {
                    e.preventDefault();
                  }
                }}
              />
              <div className="form-hint">Only numbers and + sign allowed</div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input 
                className="form-input" 
                placeholder="emmanuel@example.com" 
                type="email"
                ref={createEmailRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input 
                className="form-input" 
                type="date" 
                ref={createDobRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select 
                className="form-select" 
                ref={createGenderRef}
                defaultValue=""
              >
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Group / Department <span style={{ color: 'var(--red)' }}>*</span></label>
              <select 
                className="form-select" 
                ref={createGroupRef}
                defaultValue=""
              >
                <option value="">Select Group</option>
                {availableGroups.length > 0 ? (
                  availableGroups.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))
                ) : (
                  <option value="" disabled>No groups available</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Branch <span style={{ color: 'var(--red)' }}>*</span></label>
              <select 
                className="form-select" 
                ref={createBranchRef}
                defaultValue=""
              >
                <option value="">Select Branch</option>
                {availableBranches.length > 0 ? (
                  availableBranches.map((branch) => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))
                ) : (
                  <option value="" disabled>No branches available</option>
                )}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select 
              className="form-select" 
              ref={createStatusRef}
              defaultValue="Active"
            >
              <option value="Active">Active</option>
              <option value="Visitor">Visitor</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getCreateFormValues();
              const errors = validateForm(values);
              
              if (Object.keys(errors).length > 0) {
                showToastMessage(Object.values(errors)[0], 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createMember(values));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getMembers({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    group: groupFilter,
                    status: statusFilter
                  }));
                  showToastMessage('Member added successfully!', 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to create member', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to create member';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Adding...' : 'Add Member'}
          </button>
        </div>
      </div>
    );
  };

  // Edit Member Function
  const handleEditMember = (member) => {
    setEditingMemberId(member.id);
    
    setTimeout(() => {
      if (editFirstNameRef.current) editFirstNameRef.current.value = member.first_name || '';
      if (editLastNameRef.current) editLastNameRef.current.value = member.last_name || '';
      if (editPhoneRef.current) editPhoneRef.current.value = member.phone || '';
      if (editEmailRef.current) editEmailRef.current.value = member.email || '';
      if (editDobRef.current) editDobRef.current.value = member.dob || '';
      if (editGenderRef.current) editGenderRef.current.value = member.gender || '';
      if (editGroupRef.current) editGroupRef.current.value = member.group || '';
      if (editBranchRef.current) editBranchRef.current.value = member.branch || '';
      if (editStatusRef.current) editStatusRef.current.value = member.status || 'Active';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="Emmanuel" 
                ref={editFirstNameRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="Asante" 
                ref={editLastNameRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                placeholder="+233244123456" 
                ref={editPhoneRef}
                onChange={(e) => handlePhoneInput(e)}
                onKeyPress={(e) => {
                  const charCode = e.which ? e.which : e.keyCode;
                  if (charCode !== 43 && (charCode < 48 || charCode > 57)) {
                    e.preventDefault();
                  }
                }}
              />
              <div className="form-hint">Only numbers and + sign allowed</div>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input 
                className="form-input" 
                placeholder="emmanuel@example.com" 
                type="email"
                ref={editEmailRef}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input 
                className="form-input" 
                type="date" 
                ref={editDobRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select 
                className="form-select" 
                ref={editGenderRef}
              >
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Group / Department <span style={{ color: 'var(--red)' }}>*</span></label>
              <select 
                className="form-select" 
                ref={editGroupRef}
              >
                <option value="">Select Group</option>
                {availableGroups.length > 0 ? (
                  availableGroups.map((group) => (
                    <option key={group} value={group}>{group}</option>
                  ))
                ) : (
                  <option value="" disabled>No groups available</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Branch <span style={{ color: 'var(--red)' }}>*</span></label>
              <select 
                className="form-select" 
                ref={editBranchRef}
              >
                <option value="">Select Branch</option>
                {availableBranches.length > 0 ? (
                  availableBranches.map((branch) => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))
                ) : (
                  <option value="" disabled>No branches available</option>
                )}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select 
              className="form-select" 
              ref={editStatusRef}
            >
              <option value="Active">Active</option>
              <option value="Visitor">Visitor</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getEditFormValues();
              const errors = validateForm(values);
              
              if (Object.keys(errors).length > 0) {
                showToastMessage(Object.values(errors)[0], 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateMember({ id: member.id, data: values }));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getMembers({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    group: groupFilter,
                    status: statusFilter
                  }));
                  showToastMessage(`✅ Member updated successfully!`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to update member', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to update member';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteMember = (member) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Member</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{member.first_name} {member.last_name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteMember(member.id));
                if (result.payload?.success) {
                  closeModal();
                  dispatch(getMembers({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    group: groupFilter,
                    status: statusFilter
                  }));
                  showToastMessage('Member deleted successfully.', 'error');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to delete member', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete member';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Member'}
          </button>
        </div>
      </div>
    );
  };

  // Bulk delete selected members
  const handleBulkDelete = () => {
    if (selectedMembers.length === 0) {
      showToastMessage('Please select at least one member', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Bulk Delete Members</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{selectedMembers.length}</strong> member{selectedMembers.length !== 1 ? 's' : ''}?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(bulkDeleteMembers({ member_ids: selectedMembers }));
                if (result.payload?.success) {
                  closeModal();
                  setSelectedMembers([]);
                  setSelectAll(false);
                  dispatch(getMembers({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    group: groupFilter,
                    status: statusFilter
                  }));
                  showToastMessage(`🗑️ ${selectedMembers.length} members deleted`, 'error');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to delete members', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete members';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Selected'}
          </button>
        </div>
      </div>
    );
  };

  // Bulk action (activate/deactivate)
  const handleBulkAction = (action) => {
    if (selectedMembers.length === 0) {
      showToastMessage('Please select at least one member', 'error');
      return;
    }

    const actionLabels = {
      activate: 'Activate',
      deactivate: 'Deactivate'
    };

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> {actionLabels[action]} Members</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to <strong>{actionLabels[action].toLowerCase()}</strong> <strong>{selectedMembers.length}</strong> member{selectedMembers.length !== 1 ? 's' : ''}?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--text-mid)' 
          }}>
            Members will be {action === 'activate' ? 'activated' : 'deactivated'}.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className={`btn btn-${action === 'activate' ? 'gold' : 'danger'} btn-sm`}
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(bulkActionMembers({ 
                  member_ids: selectedMembers,
                  action: action
                }));
                if (result.payload?.success) {
                  closeModal();
                  setSelectedMembers([]);
                  setSelectAll(false);
                  dispatch(getMembers({ 
                    per_page: itemsPerPage, 
                    page: currentPage,
                    search: searchTerm,
                    group: groupFilter,
                    status: statusFilter
                  }));
                  showToastMessage(`✅ ${selectedMembers.length} members ${action === 'activate' ? 'activated' : 'deactivated'}`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || `Failed to ${action} members`, 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || `Failed to ${action} members`;
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Processing...' : `Yes, ${actionLabels[action]}`}
          </button>
        </div>
      </div>
    );
  };

  const handleSendSms = (member) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Mail size={16} style={{ verticalAlign: '-3px' }} /> Send SMS to {member.first_name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Recipient</label>
            <input 
              className="form-input" 
              value={`${member.first_name} ${member.last_name} (${member.phone})`} 
              disabled
              style={{ opacity: 0.7 }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea 
              className="form-textarea" 
              placeholder={`Type a message to ${member.first_name}…`} 
              rows="4"
              ref={(el) => {
                if (el) {
                  el.value = `Hello ${member.first_name}, this is a message from FaithConnect.`;
                }
              }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(sendSmsToMember({ 
                  id: member.id, 
                  message: document.querySelector('.modal-body textarea')?.value || ''
                }));
                if (result.payload?.success) {
                  closeModal();
                  showToastMessage(`📨 SMS sent to ${member.first_name}!`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to send SMS', 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to send SMS';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Sending...' : 'Send SMS'}
          </button>
        </div>
      </div>
    );
  };

  const handleViewMember = (member) => {
    dispatch(getMember(member.id));
    const color = getRandomColor(member.id);
    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="member-row-avatar" style={{ background: color, width: '46px', height: '46px', fontSize: '16px' }}>
              {getInitials(`${member.first_name} ${member.last_name}`)}
            </div>
            <div>
              <div className="modal-title">{member.first_name} {member.last_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {member.phone} · {member.group}
                {member.member_code && (
                  <span style={{ marginLeft: '8px', fontFamily: 'JetBrains Mono, monospace', fontSize: '10px' }}>
                    #{member.member_code}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="grid-2" style={{ gap: '12px', marginBottom: '16px' }}>
            {[
              ['Status', `<span class="badge ${member.status === 'Active' ? 'badge-green' : member.status === 'Inactive' ? 'badge-red' : 'badge-amber'}">${member.status}</span>`],
              ['Branch', member.branch],
              ['Joined', formatDate(member.created_at)],
              ['Birthday', member.dob || 'N/A'],
              ['Gender', member.gender === 'M' ? 'Male' : member.gender === 'F' ? 'Female' : 'Other'],
              ['Engagement', `${member.engagement_score || 0}/100`]
            ].map(([l, v]) => (
              <div key={l} style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>{l}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }} dangerouslySetInnerHTML={{ __html: v }}></div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => { 
              closeModal();
              setTimeout(() => handleDeleteMember(member), 100);
            }}
            title="Delete member"
          >
            Delete
          </button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => { 
              closeModal();
              setTimeout(() => handleSendSms(member), 100);
            }}
            title="Send SMS to member"
          >
            Send SMS
          </button>
        </div>
      </div>
    );
  };

  // ==================== BULK IMPORT HANDLER ====================
  const handleBulkImport = () => {
    navigate('/members/import');
  };

  // ==================== RENDER ====================

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '10px', flex: '1', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: '1', minWidth: '150px' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input
              type="text"
              placeholder="Search by name, phone…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <select 
            className="form-select" 
            style={{ width: '160px' }} 
            value={groupFilter} 
            onChange={(e) => {
              setGroupFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Groups</option>
            {availableGroups.length > 0 ? (
              availableGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))
            ) : (
              <option value="" disabled>Loading groups...</option>
            )}
          </select>
          <select 
            className="form-select" 
            style={{ width: '160px', marginLeft: '8px' }} 
            value={branchFilter} 
            onChange={(e) => {
              setBranchFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Branches</option>
            {availableBranches.length > 0 ? (
              availableBranches.map(branch => (
                <option key={branch} value={branch}>{branch}</option>
              ))
            ) : (
              <option value="" disabled>Loading branches...</option>
            )}
          </select>
          <select 
            className="form-select" 
            style={{ width: '140px' }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Visitor">Visitor</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedMembers.length > 0 && (
            <>
              <button className="btn btn-success btn-sm" onClick={() => handleBulkAction('activate')}>
                <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Activate ({selectedMembers.length})
              </button>
              <button className="btn btn-warning btn-sm" onClick={() => handleBulkAction('deactivate')}>
                <Ban size={14} style={{ verticalAlign: '-2px' }} /> Deactivate ({selectedMembers.length})
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                <Trash2 size={14} style={{ verticalAlign: '-2px' }} /> Delete ({selectedMembers.length})
              </button>
            </>
          )}
          <button className="btn btn-gold btn-sm" onClick={handleAddMember} title="Add new member">
            + Add Member
          </button>
          <button className="btn btn-gold btn-sm" onClick={handleBulkImport} title="Bulk import members">
            <Download size={14} style={{ verticalAlign: '-2px' }} /> Bulk Import
          </button>
          <button className="btn btn-ghost btn-sm" onClick={openExportModal} title="Export members">
            ⬇ Export
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading members...</div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input 
                      type="checkbox" 
                      style={{ accentColor: 'var(--navy)' }} 
                      checked={selectAll && members.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Member</th>
                  <th>Phone</th>
                  <th>Group</th>
                  <th>Branch</th>
                  <th>Status</th>
                  <th>Engagement</th>
                  <th style={{ minWidth: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.length > 0 ? (
                  members.map(m => {
                    const color = getRandomColor(m.id);
                    const statusBadge = m.status === 'Active' ? 'badge-green' : m.status === 'Inactive' ? 'badge-red' : 'badge-amber';
                    const scoreColor = (m.engagement_score || 0) >= 80 ? 'var(--green)' : (m.engagement_score || 0) >= 50 ? 'var(--amber)' : 'var(--red)';
                    return (
                      <tr key={m.id}>
                        <td>
                          <input 
                            type="checkbox" 
                            style={{ accentColor: 'var(--navy)' }} 
                            checked={selectedMembers.includes(m.id)}
                            onChange={() => handleSelectMember(m.id)}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="member-row-avatar" style={{ background: color }}>{getInitials(`${m.first_name} ${m.last_name}`)}</div>
                            <div>
                              <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{m.first_name} {m.last_name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                                {m.member_code && (
                                  <span style={{ fontFamily: 'JetBrains Mono, monospace', marginRight: '8px' }}>#{m.member_code}</span>
                                )}
                                Joined {formatDate(m.created_at)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>
                            {formatPhoneNumber(m.phone)}
                          </div>
                          {m.email && (
                            <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>{m.email}</div>
                          )}
                        </td>
                        <td><span className="badge badge-navy">{m.group}</span></td>
                        <td style={{ fontSize: '12px', color: 'var(--text-mid)' }}>{m.branch}</td>
                        <td><span className={`badge ${statusBadge}`}>{m.status}</span></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="progress-bar" style={{ width: '80px' }}>
                              <div className="progress-fill" style={{ width: `${m.engagement_score || 0}%`, background: scoreColor }}></div>
                            </div>
                            <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: scoreColor }}>{m.engagement_score || 0}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleSendSms(m)}
                              title={`📨 Send SMS to ${m.first_name}`}
                            >
                              <Mail size={16} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleViewMember(m)}
                              title={`👁️ View ${m.first_name}'s profile`}
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm btn-icon"
                              onClick={() => handleEditMember(m)}
                              title={`✏️ Edit ${m.first_name}`}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              className="btn btn-danger btn-sm btn-icon"
                              onClick={() => handleDeleteMember(m)}
                              title={`🗑️ Delete ${m.first_name}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8">
                      <div className="empty-state">
                        <div className="empty-icon"><Search size={40} strokeWidth={1.5} /></div>
                        <div className="empty-title">No members found</div>
                        <div className="empty-text">Try adjusting your search or filters</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalItems > 0 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
              padding: '14px 0 0',
              borderTop: '1px solid var(--border)',
              marginTop: '4px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> members
                {selectedMembers.length > 0 && (
                  <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                    · {selectedMembers.length} selected
                  </span>
                )}
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                  <select 
                    className="form-select" 
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    title="Items per page"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    style={{ 
                      opacity: currentPage === 1 ? 0.5 : 1, 
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
                    }}
                    title="Previous page"
                  >
                    ← Prev
                  </button>
                  
                  {getPageNumbers().map(page => (
                    <button 
                      key={page}
                      className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => goToPage(page)}
                      title={`Go to page ${page}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    style={{ 
                      opacity: currentPage === totalPages ? 0.5 : 1, 
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' 
                    }}
                    title="Next page"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        .btn-success {
          background: #22c55e !important;
          color: white !important;
          border: none !important;
        }
        .btn-success:hover {
          background: #16a34a !important;
          color: white !important;
        }
        .btn-warning {
          background: #f59e0b !important;
          color: white !important;
          border: none !important;
        }
        .btn-warning:hover {
          background: #d97706 !important;
          color: white !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
          min-width: 28px;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        @media (max-width: 1024px) {
          .members-table .action-buttons {
            flex-wrap: wrap;
          }
        }
        @media (max-width: 768px) {
          .members-table .filters {
            flex-direction: column;
          }
          .members-table .filters .search-box {
            max-width: 100%;
          }
          .members-table .filters select {
            width: 100% !important;
          }
          .members-table .actions {
            flex-direction: column;
            width: 100%;
          }
          .members-table .actions button {
            width: 100%;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 700px;
          }
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default Members;